import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Alert, Spinner, Pagination } from 'react-bootstrap';
import api from '../../services/api';
const Billing = () => {
  const [summary, setSummary] = useState(null);
  const [asClient, setAsClient] = useState({ data: [], total: 0, page: 1 });
  const [asSupplier, setAsSupplier] = useState({ data: [], total: 0, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    loadData();
  }, []);
  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryRes, clientRes, supplierRes] = await Promise.all([
        api.get('/billing/summary'),
        api.get('/billing/as-client?page=1'),
        api.get('/billing/as-supplier?page=1')
      ]);
      setSummary(summaryRes.data);
      setAsClient({ data: clientRes.data.data, total: clientRes.data.total, page: 1 });
      setAsSupplier({ data: supplierRes.data.data, total: supplierRes.data.total, page: 1 });
      setLoading(false);
    } catch (error) {
      console.error('Failed to load billing data:', error);
      setError('Failed to load billing data');
      setLoading(false);
    }
  };
  const loadAsClientPage = async (page) => {
    try {
      const response = await api.get(`/billing/as-client?page=${page}`);
      setAsClient({ data: response.data.data, total: response.data.total, page });
    } catch (error) {
      console.error('Failed to load client data:', error);
    }
  };
  const loadAsSupplierPage = async (page) => {
    try {
      const response = await api.get(`/billing/as-supplier?page=${page}`);
      setAsSupplier({ data: response.data.data, total: response.data.total, page });
    } catch (error) {
      console.error('Failed to load supplier data:', error);
    }
  };
  const renderPagination = (total, currentPage, onPageChange) => {
    const totalPages = Math.ceil(total / 15);
    if (totalPages <= 1) return null;
    const items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item key={number} active={number === currentPage} onClick={() => onPageChange(number)}>
          {number}
        </Pagination.Item>
      );
    }
    return <Pagination>{items}</Pagination>;
  };
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }
  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }
  const isAttention = summary?.details_flag?.includes('ATTENTION');
  const formatCurrency = (value) => {
    if (!value) return '0.00';
    return parseFloat(value).toFixed(2);
  };
  return (
    <Container fluid className="p-4">
      <style>{`
        .billing-table th {
          font-size: 0.85rem;
          padding: 0.5rem 0.4rem !important;
          white-space: nowrap;
        }
        .billing-table td {
          font-size: 0.85rem;
          padding: 0.4rem 0.4rem !important;
          vertical-align: middle;
        }
        .amount-invoice {
          color: #2d5016 !important;
          font-weight: 600;
          text-align: right;
        }
        .amount-payment {
          color: #52c41a !important;
          font-weight: 600;
          text-align: right;
        }
        .date-cell {
          white-space: nowrap;
          font-size: 0.8rem;
        }
        .status-yes {
          color: #52c41a !important;
          font-weight: 500;
        }
        .status-no {
          color: #ff4d4f !important;
          font-weight: 500;
        }
        .summary-table td {
          padding: 0.6rem !important;
        }
        .summary-amount {
          font-weight: 600;
          font-size: 1.1rem;
        }
      `}</style>
      <h2 className="mb-4">Billing</h2>
      {summary && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Summary</Card.Title>
            <Table bordered className="summary-table">
              <tbody>
                <tr>
                  <td><strong>Total Invoiced Balance:</strong></td>
                  <td className="summary-amount" style={{ color: '#2d5016' }}>{formatCurrency(summary.total_invoiced_balance)}</td>
                </tr>
                <tr>
                  <td><strong>VOIP UnBilled Balance:</strong></td>
                  <td className="summary-amount" style={{ color: '#2d5016' }}>{formatCurrency(summary.voip_unbilled_balance)}</td>
                </tr>
                <tr>
                  <td><strong>SMS UnBilled Balance:</strong></td>
                  <td className="summary-amount" style={{ color: '#2d5016' }}>{formatCurrency(summary.sms_unbilled_balance)}</td>
                </tr>
                <tr>
                  <td><strong>Billed To Date:</strong></td>
                  <td className="summary-amount" style={{ color: '#2d5016' }}>{summary.billed_to_date}</td>
                </tr>
                <tr>
                  <td><strong>Bank Details Status:</strong></td>
                  <td style={{ color: isAttention ? '#DF0174' : '#088A08' }}>
                    {summary.details_flag}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>As Client</Card.Title>
          <Table striped bordered hover responsive className="billing-table">
            <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <tr>
                <th>Invoice Date</th>
                <th>Out Inv Number</th>
                <th>Supplier</th>
                <th>Client</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>Accepted Inv Sum</th>
                <th>Paid Balance</th>
                <th>Payment Date</th>
                <th>Accepted</th>
                <th>Disputed</th>
              </tr>
            </thead>
            <tbody>
              {asClient.data.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center">No records found</td>
                </tr>
              ) : (
                asClient.data.map((row, idx) => (
                  <tr key={idx}>
                    <td className="date-cell">{row.inv_date_c}</td>
                    <td>{row.out_inv_number}</td>
                    <td>{row.supplier}</td>
                    <td>{row.client}</td>
                    <td className="date-cell">{row.from_date}</td>
                    <td className="date-cell">{row.to_date}</td>
                    <td className="amount-invoice">{formatCurrency(row.accepted_inv_sum)}</td>
                    <td className="amount-payment">{formatCurrency(row.paid_balance)}</td>
                    <td className="date-cell">{row.payment_date}</td>
                    <td className={row.accepted === 'YES' ? 'status-yes' : 'status-no'}>{row.accepted}</td>
                    <td className={row.disputed === 'YES' ? 'status-no' : 'status-yes'}>{row.disputed}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {renderPagination(asClient.total, asClient.page, loadAsClientPage)}
        </Card.Body>
      </Card>
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>As Supplier</Card.Title>
          <Table striped bordered hover responsive className="billing-table">
            <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <tr>
                <th>Invoice Date</th>
                <th>Cust Inv Number</th>
                <th>Supplier</th>
                <th>Client</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>Accepted Inv Sum</th>
                <th>Paid Balance</th>
                <th>Payment Date</th>
                <th>Accepted</th>
                <th>Disputed</th>
              </tr>
            </thead>
            <tbody>
              {asSupplier.data.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center">No records found</td>
                </tr>
              ) : (
                asSupplier.data.map((row, idx) => (
                  <tr key={idx}>
                    <td className="date-cell">{row.inv_date_c}</td>
                    <td>{row.cust_inv_number}</td>
                    <td>{row.supplier}</td>
                    <td>{row.client}</td>
                    <td className="date-cell">{row.from_date}</td>
                    <td className="date-cell">{row.to_date}</td>
                    <td className="amount-invoice">{formatCurrency(row.accepted_inv_sum)}</td>
                    <td className="amount-payment">{formatCurrency(row.paid_balance)}</td>
                    <td className="date-cell">{row.payment_date}</td>
                    <td className={row.accepted === 'YES' ? 'status-yes' : 'status-no'}>{row.accepted}</td>
                    <td className={row.disputed === 'YES' ? 'status-no' : 'status-yes'}>{row.disputed}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {renderPagination(asSupplier.total, asSupplier.page, loadAsSupplierPage)}
        </Card.Body>
      </Card>
    </Container>
  );
};
export default Billing;